import {
  EstimatedUsageType,
  type AddressMeterPointDetails,
  type BrokerCookie,
  type CreditCheckResponse,
  type ReferralInformationResponse,
} from "@/api/model"
import type { EnrolmentAddressRequest } from "@/api/model/enrolmentAddressRequest"
import type { EnrolmentPaymentMethodRequest } from "@/api/model/enrolmentPaymentMethodRequest"
import { FuelType } from "@/api/model/fuelType"
import type { OfgemUsageProfilesResponse } from "@/api/model/ofgemUsageProfilesResponse"
import type { QuotesResponse } from "@/api/model/quotesResponse"
import type { SmartMeterReadingFrequency } from "@/api/model/smartMeterReadingFrequency"
import { ref } from "vue"

export const DEFAULT_OPTIONS = {
  YES: "yes",
  NO: "no",
  UNSURE: "unsure",
} as const
export type DefaultOptionType =
  (typeof DEFAULT_OPTIONS)[keyof typeof DEFAULT_OPTIONS]

export const EV_SUB_STEPS = {
  TARIFF_SELECTION: "tariffSelection",
  ELIGIBILITY: "evEligibility",
  INTERIM_TARIFF: "evInterimTariff",
} as const
export type EvSubStep = (typeof EV_SUB_STEPS)[keyof typeof EV_SUB_STEPS]

export type EnrolmentAddress = {
  addressLine1: string
  addressLine2?: string
  addressLine3?: string
  postcode: string
  countryCode: string
  mpan?: string
  mprn?: string
  isMpanSmartMeter?: boolean
}

export type UsageLevel = keyof OfgemUsageProfilesResponse
export type EnrolmentUsageType = Exclude<
  EstimatedUsageType,
  typeof EstimatedUsageType.EV_OWNER
>

export type EnrolmentProperty = {
  selectedFuelType?: FuelType
  evTariffInterest?: DefaultOptionType
  hasMultirateMeter?: DefaultOptionType
  usageEstimateType?: EnrolmentUsageType
  electricityKwh?: number
  gasKwh?: number
}

export type EnrolmentTariff = Partial<QuotesResponse> & {
  seasonalPayments?: boolean
}

export type EnrolmentPersonalDetails = {
  title?: string
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: string
  marketingOptIn: boolean
  vulnerableCustomer: boolean
  futureStartDate?: string
  meterReadFrequency?: SmartMeterReadingFrequency
}

export type EnrolmentPayment = Partial<EnrolmentPaymentMethodRequest>

export type EnrolmentEv = {
  isMeterEligible?: boolean
  meterEligibilityError?: string
  eligibilityConfirmed?: boolean
  evTariffMarketingConsent?: boolean
}

export type EnrolmentExtras = {
  fullAddress: string
  referralCode?: string
  referral?: ReferralInformationResponse
  referralError?: boolean
  voucherCode?: string
  brokerCookie?: BrokerCookie
  thirdPartySwitchReference?: string
  /** Paid-ad campaign (utm_campaign|campaign id), sent only when a paid click supplies the broker */
  campaignReference?: string
  directDebitAuthorisation: boolean
  creditCheckConsent: boolean
  /** Result of the enrolment credit check; its prospectId is sent with the enrolment */
  creditCheck?: CreditCheckResponse
  /** Serialised credit check request that produced `creditCheck`, to detect changed details */
  creditCheckRequestKey?: string
  tariffStartDate?: string
  isManualEntry?: boolean
  /** True when the address selected/entered did not already have an MPRN — MPRN must be collected on the Property step */
  missingMPRN?: boolean
}

export type EvaluateGasParams = {
  isDualFuel: boolean
  missingMPRN: boolean
  isManualEntry: boolean
  mprnOnlyAddressCount: number
  isMprnNotListed: boolean
}

/**
 * Pure function — determines whether the MPRN dropdown and/or input should be shown
 * on the Property step. Extracted for testability.
 */
export function evaluateIfNeedGas(params: EvaluateGasParams): {
  showDropdown: boolean
  showInput: boolean
} {
  const {
    isDualFuel,
    missingMPRN,
    isManualEntry,
    mprnOnlyAddressCount,
    isMprnNotListed,
  } = params

  if (!isDualFuel || !missingMPRN) {
    return { showDropdown: false, showInput: false }
  }

  const showDropdown = !isManualEntry && mprnOnlyAddressCount > 0
  const showInput =
    isManualEntry || mprnOnlyAddressCount === 0 || isMprnNotListed

  return { showDropdown, showInput }
}

const address = ref<EnrolmentAddress>({
  addressLine1: "",
  postcode: "",
  countryCode: "",
})

const property = ref<EnrolmentProperty>({
  selectedFuelType: undefined,
  evTariffInterest: undefined,
  hasMultirateMeter: undefined,
  usageEstimateType: undefined,
  electricityKwh: undefined,
  gasKwh: undefined,
})

const tariff = ref<EnrolmentTariff>({})

const personalDetails = ref<EnrolmentPersonalDetails>({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  marketingOptIn: false,
  vulnerableCustomer: false,
})

const payment = ref<EnrolmentPayment>({
  bankAccountName: "",
  bankAccountNumber: "",
  bankAccountSortCode: "",
  paymentDayOfMonth: 0,
  paymentAmount: 0,
})

const extras = ref<EnrolmentExtras>({
  fullAddress: "",
  directDebitAuthorisation: false,
  creditCheckConsent: false,
})

const historicAddress = ref<EnrolmentAddressRequest | null>(null)

const quotes = ref<QuotesResponse[] | null>(null)

const ofgemUsageProfiles = ref<OfgemUsageProfilesResponse | null>(null)

const mprnOnlyAddresses = ref<AddressMeterPointDetails[]>([])
const isMprnNotListed = ref(false)

const ev = ref<EnrolmentEv>({})

const evSubStep = ref<EvSubStep>(EV_SUB_STEPS.TARIFF_SELECTION)

const crossFlowAddress = ref<AddressMeterPointDetails | null>(null)

const enrolmentId = ref<number>()

export const useEnrolment = () => {
  const updateAddress = (data: Partial<EnrolmentAddress>) => {
    address.value = { ...address.value, ...data }
  }

  const updateProperty = (data: Partial<EnrolmentProperty>) => {
    property.value = { ...property.value, ...data }
  }

  const updateTariff = (data: Partial<EnrolmentTariff>) => {
    tariff.value = { ...tariff.value, ...data }
  }

  const updatePersonalDetails = (data: Partial<EnrolmentPersonalDetails>) => {
    personalDetails.value = { ...personalDetails.value, ...data }
  }

  const updatePayment = (data: Partial<EnrolmentPayment>) => {
    payment.value = { ...payment.value, ...data }
  }

  const updateEv = (data: Partial<EnrolmentEv>) => {
    ev.value = { ...ev.value, ...data }
  }

  const resetEvState = () => {
    ev.value = {}
    evSubStep.value = EV_SUB_STEPS.TARIFF_SELECTION
  }

  const updateExtras = (data: Partial<EnrolmentExtras>) => {
    extras.value = { ...extras.value, ...data }
  }

  const updateHistoricAddress = (data: EnrolmentAddressRequest | null) => {
    historicAddress.value = data
  }

  const updateQuotes = (data: QuotesResponse[]) => {
    quotes.value = data
  }

  const resetTariff = () => {
    tariff.value = {}
  }

  const clearQuotes = () => {
    quotes.value = null
    resetTariff()
  }

  const updateOfgemUsageProfiles = (data: OfgemUsageProfilesResponse) => {
    ofgemUsageProfiles.value = data
  }

  const updateMprnOnlyAddresses = (data: AddressMeterPointDetails[]) => {
    mprnOnlyAddresses.value = data
  }

  const resetEnrolment = () => {
    address.value = {
      addressLine1: "",
      postcode: "",
      countryCode: "",
    }
    property.value = {
      selectedFuelType: undefined,
      evTariffInterest: undefined,
      hasMultirateMeter: undefined,
      usageEstimateType: undefined,
      electricityKwh: undefined,
      gasKwh: undefined,
    }
    resetTariff()
    personalDetails.value = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      marketingOptIn: false,
      vulnerableCustomer: false,
    }
    payment.value = {
      bankAccountName: "",
      bankAccountNumber: "",
      bankAccountSortCode: "",
      paymentDayOfMonth: 0,
      paymentAmount: 0,
    }
    extras.value = {
      fullAddress: "",
      directDebitAuthorisation: false,
      creditCheckConsent: false,
    }
    ev.value = {}
    evSubStep.value = EV_SUB_STEPS.TARIFF_SELECTION
    quotes.value = null
    historicAddress.value = null
    ofgemUsageProfiles.value = null
    mprnOnlyAddresses.value = []
    isMprnNotListed.value = false
    crossFlowAddress.value = null
  }

  const getCrossFlowAddress = (): AddressMeterPointDetails | null => {
    return crossFlowAddress.value
  }

  const setCrossFlowAddress = (data: AddressMeterPointDetails) => {
    crossFlowAddress.value = data
  }

  const clearCrossFlowAddress = () => {
    crossFlowAddress.value = null
  }

  return {
    address,
    property,
    tariff,
    personalDetails,
    payment,
    extras,
    ev,
    evSubStep,
    quotes,
    enrolmentId,
    updateAddress,
    updateProperty,
    updateTariff,
    updatePersonalDetails,
    updatePayment,
    updateEv,
    resetEvState,
    updateExtras,
    updateQuotes,
    clearQuotes,
    historicAddress,
    updateHistoricAddress,
    ofgemUsageProfiles,
    updateOfgemUsageProfiles,
    mprnOnlyAddresses,
    updateMprnOnlyAddresses,
    isMprnNotListed,
    resetEnrolment,
    getCrossFlowAddress,
    setCrossFlowAddress,
    clearCrossFlowAddress,
  }
}
